import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../context/AdminAuthContext';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import AdminLayout from '../components/AdminLayout';
import AdminFilterBar, { AdminSearchField } from '../components/ui/AdminFilterBar';
import AdminPagination from '../components/ui/AdminPagination';
import { AdminLoadingState, AdminEmptyState } from '../components/ui/AdminStates';
import Button from '../components/ui/Button';
import './PagePolo.css';

interface Polo {
  id_acting: number;
  state: string;
  city: string;
  polo: string;
  address: string;
  admin_name: string;
}

const PAGE_SIZE = 10;

// Lista completa de cidades do estado do Rio de Janeiro
const rioDeJaneiroCities = [
  'Angra dos Reis', 'Aperibé', 'Araruama', 'Areal', 'Armação dos Búzios', 'Arraial do Cabo',
  'Barra do Piraí', 'Barra Mansa', 'Belford Roxo', 'Bom Jardim', 'Bom Jesus do Itabapoana',
  'Cabo Frio', 'Cachoeiras de Macacu', 'Cambuci', 'Campos dos Goytacazes', 'Cantagalo',
  'Carapebus', 'Cardoso Moreira', 'Carmo', 'Casimiro de Abreu', 'Comendador Levy Gasparian',
  'Conceição de Macabu', 'Cordeiro', 'Duas Barras', 'Duque de Caxias', 'Engenheiro Paulo de Frontin',
  'Guapimirim', 'Iguaba Grande', 'Itaboraí', 'Itaguaí', 'Italva', 'Itaocara', 'Itaperuna',
  'Itatiaia', 'Japeri', 'Laje do Muriaé', 'Macaé', 'Macuco', 'Magé', 'Mangaratiba', 'Maricá',
  'Mendes', 'Mesquita', 'Miguel Pereira', 'Miracema', 'Natividade', 'Nilópolis', 'Niterói',
  'Nova Friburgo', 'Nova Iguaçu', 'Paracambi', 'Paraíba do Sul', 'Paraty', 'Paty do Alferes',
  'Petrópolis', 'Pinheiral', 'Piraí', 'Porciúncula', 'Porto Real', 'Quatis', 'Queimados',
  'Quissamã', 'Resende', 'Rio Bonito', 'Rio Claro', 'Rio das Flores', 'Rio das Ostras',
  'Rio de Janeiro', 'Santa Maria Madalena', 'Santo Antônio de Pádua', 'São Fidélis',
  'São Francisco de Itabapoana', 'São Gonçalo', 'São João da Barra', 'São João de Meriti',
  'São José de Ubá', 'São José do Vale do Rio Preto', 'São Pedro da Aldeia', 'São Sebastião do Alto',
  'Sapucaia', 'Saquarema', 'Seropédica', 'Silva Jardim', 'Sumidouro', 'Tanguá', 'Teresópolis',
  'Trajano de Moraes', 'Três Rios', 'Valença', 'Varre-Sai', 'Vassouras', 'Volta Redonda',
];

const PagePolo: React.FC = () => {
  const { adminToken, logoutAdmin } = useAdminAuth();
  const navigate = useNavigate();

  const [polos, setPolos] = useState<Polo[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    state: 'RJ',
    city: '',
    polo: '',
    address: '',
    admin_name: '',
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [editingPolo, setEditingPolo] = useState<Polo | null>(null);
  const [editFormData, setEditFormData] = useState({
    state: '',
    city: '',
    polo: '',
    address: '',
    admin_name: '',
  });

  // Função para buscar polos via GET
  const fetchPolos = useCallback(async () => {
    if (!adminToken) {
      navigate('/rj/admin-login');
      return;
    }
    try {
      const response = await axios.get<{ data: Polo[] }>('https://prevcenso.com/backend/api/acting', {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setPolos(response.data.data);
      console.log('Polos:', response.data.data);
    } catch (error: any) {
      console.error('Erro ao carregar polos:', {
        message: error.response?.data?.message || error.message,
        status: error.response?.status,
        url: error.config?.url,
      });
      alert('Falha ao carregar dados dos polos.');
    } finally {
      setLoading(false);
    }
  }, [adminToken, navigate]);

  // Função para gerar e baixar o PDF
  const handleGeneratePDF = () => {
    if (polos.length === 0) {
      alert('Nenhum polo disponível para gerar o PDF.');
      return;
    }

    try {
      const doc = new jsPDF();

      // Adicionar título
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Censo Prev', 20, 20);

      // Adicionar subtítulo
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Lista de Polos', 20, 30);

      // Adicionar tabela
      autoTable(doc, {
        startY: 40,
        head: [['ID Polo', 'Estado', 'Cidade', 'Nome do Polo', 'Endereço', 'Nome do Administrador']],
        body: polos.map((polo) => [
          polo.id_acting.toString(),
          polo.state,
          polo.city,
          polo.polo,
          polo.address,
          polo.admin_name,
        ]),
        styles: {
          font: 'helvetica',
          fontSize: 10,
          cellPadding: 4,
        },
        headStyles: {
          fillColor: [25, 85, 191],
          textColor: [255, 255, 255],
          fontStyle: 'bold',
        },
        alternateRowStyles: {
          fillColor: [240, 240, 240],
        },
        tableWidth: 'auto',
        margin: { top: 40, left: 20, right: 20 },
        columnStyles: {
          0: { cellWidth: 15 }, // ID Polo
          1: { cellWidth: 20 }, // Estado
          2: { cellWidth: 25 }, // Cidade
          3: { cellWidth: 30 }, // Nome do Polo
          4: { cellWidth: 40 }, // Endereço
          5: { cellWidth: 30 }, // Nome do Administrador
        },
      });

      // Salvar o PDF
      doc.save('relatorio_polos.pdf');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Falha ao gerar o PDF. Verifique se os pacotes jspdf e jspdf-autotable estão corretamente instalados.');
    }
  };

  useEffect(() => {
    fetchPolos();
  }, [fetchPolos]);

  // Função para criar um novo polo via POST
  const handleCreatePolo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminToken) {
      navigate('/rj/admin-login');
      return;
    }

    if (!formData.state || !formData.city || !formData.polo || !formData.address || !formData.admin_name) {
      setFormError('Todos os campos são obrigatórios.');
      return;
    }

    try {
      await axios.post(
        'https://prevcenso.com/backend/api/acting',
        {
          state: formData.state,
          city: formData.city,
          polo: formData.polo,
          address: formData.address,
          admin_name: formData.admin_name,
        },
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        }
      );
      setFormError(null);
      setFormData({ state: 'RJ', city: '', polo: '', address: '', admin_name: '' });
      alert('Polo criado com sucesso!');
      fetchPolos();
    } catch (error: any) {
      console.error('Erro ao criar polo:', {
        message: error.response?.data?.message || error.message,
        status: error.response?.status,
        url: error.config?.url,
      });
      setFormError(error.response?.data?.message || 'Falha ao criar polo. Verifique os dados e tente novamente.');
    }
  };

  // Função para editar um polo via PUT
  const handleEditPolo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!adminToken || !editingPolo) {
      navigate('/rj/admin-login');
      return;
    }

    if (!editFormData.state || !editFormData.city || !editFormData.polo || !editFormData.address || !editFormData.admin_name) {
      setFormError('Todos os campos são obrigatórios para edição.');
      return;
    }

    try {
      await axios.put(
        `https://prevcenso.com/backend/api/acting/${editingPolo.id_acting}`,
        {
          state: editFormData.state,
          city: editFormData.city,
          polo: editFormData.polo,
          address: editFormData.address,
          admin_name: editFormData.admin_name,
        },
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        }
      );
      setFormError(null);
      setEditingPolo(null);
      setEditFormData({ state: '', city: '', polo: '', address: '', admin_name: '' });
      alert('Polo atualizado com sucesso!');
      fetchPolos();
    } catch (error: any) {
      console.error('Erro ao atualizar polo:', {
        message: error.response?.data?.message || error.message,
        status: error.response?.status,
        url: error.config?.url,
      });
      setFormError(error.response?.data?.message || 'Falha ao atualizar polo. Verifique os dados e tente novamente.');
    }
  };

  // Função para excluir um polo via DELETE
  const handleDeletePolo = async (id: number) => {
    if (!adminToken) {
      navigate('/rj/admin-login');
      return;
    }

    if (!window.confirm('Tem certeza que deseja excluir este polo?')) {
      return;
    }

    try {
      await axios.delete(`https://prevcenso.com/backend/api/acting/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      alert('Polo excluído com sucesso!');
      fetchPolos();
    } catch (error: any) {
      console.error('Erro ao excluir polo:', {
        message: error.response?.data?.message || error.message,
        status: error.response?.status,
        url: error.config?.url,
      });
      alert(error.response?.data?.message || 'Falha ao excluir polo. Tente novamente.');
    }
  };

  // Função para iniciar edição de um polo
  const startEditing = (polo: Polo) => {
    setEditingPolo(polo);
    setEditFormData({
      state: polo.state,
      city: polo.city,
      polo: polo.polo,
      address: polo.address,
      admin_name: polo.admin_name,
    });
  };

  // Função para cancelar edição
  const cancelEditing = () => {
    setEditingPolo(null);
    setEditFormData({ state: '', city: '', polo: '', address: '', admin_name: '' });
    setFormError(null);
  };

  // Filtro de busca (cidade, nome do polo, estado ou administrador)
  const filteredPolos = polos.filter((p) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      p.polo.toLowerCase().includes(q) ||
      p.city.toLowerCase().includes(q) ||
      p.state.toLowerCase().includes(q) ||
      p.admin_name.toLowerCase().includes(q)
    );
  });

  // Paginação
  const totalPages = Math.ceil(filteredPolos.length / PAGE_SIZE) || 1;
  const pagedData = filteredPolos.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const chips = [
    searchTerm && { key: 'search', label: `Busca: ${searchTerm}`, onRemove: () => setSearchTerm('') },
  ].filter(Boolean) as { key: string; label: string; onRemove: () => void }[];

  return (
    <AdminLayout title="Gerenciamento de Polos">
      <section className="admin-card">
        <div className="admin-card__head">
          <h2>Criar Novo Polo</h2>
        </div>
        <form onSubmit={handleCreatePolo} className="admin-form-grid">
          <div className="admin-form-group">
            <label htmlFor="state">Estado</label>
            <select
              id="state"
              value={formData.state}
              onChange={(e) => setFormData({ ...formData, state: e.target.value })}
              className="admin-select"
            >
              <option value="RJ">Rio de Janeiro</option>
              <option value="SP">São Paulo</option>
            </select>
          </div>
          <div className="admin-form-group">
            <label htmlFor="city">Cidade</label>
            <select
              id="city"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="admin-select"
            >
              <option value="">Selecione uma cidade</option>
              {formData.state === 'RJ' ? (
                rioDeJaneiroCities.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))
              ) : (
                <option value="São Paulo">São Paulo</option>
              )}
            </select>
          </div>
          <div className="admin-form-group">
            <label htmlFor="polo">Nome do Polo</label>
            <input
              id="polo"
              type="text"
              value={formData.polo}
              onChange={(e) => setFormData({ ...formData, polo: e.target.value })}
              className="admin-input"
              placeholder="Ex: Polo Central"
            />
          </div>
          <div className="admin-form-group">
            <label htmlFor="address">Endereço</label>
            <input
              id="address"
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="admin-input"
              placeholder="Ex: Rua Exemplo, 123"
            />
          </div>
          <div className="admin-form-group">
            <label htmlFor="admin_name">Nome do Administrador</label>
            <input
              id="admin_name"
              type="text"
              value={formData.admin_name}
              onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
              className="admin-input"
              placeholder="Ex: João Silva"
            />
          </div>
          {formError && !editingPolo && <p className="admin-form-error">{formError}</p>}
          <div className="admin-form-actions">
            <Button type="submit">Criar Polo</Button>
          </div>
        </form>
      </section>

      {editingPolo && (
        <section className="admin-card">
          <div className="admin-card__head">
            <h2>Editar Polo</h2>
          </div>
          <form onSubmit={handleEditPolo} className="admin-form-grid">
            <div className="admin-form-group">
              <label htmlFor="edit-state">Estado</label>
              <select
                id="edit-state"
                value={editFormData.state}
                onChange={(e) => setEditFormData({ ...editFormData, state: e.target.value })}
                className="admin-select"
              >
                <option value="RJ">Rio de Janeiro</option>
                <option value="SP">São Paulo</option>
              </select>
            </div>
            <div className="admin-form-group">
              <label htmlFor="edit-city">Cidade</label>
              <select
                id="edit-city"
                value={editFormData.city}
                onChange={(e) => setEditFormData({ ...editFormData, city: e.target.value })}
                className="admin-select"
              >
                <option value="">Selecione uma cidade</option>
                {editFormData.state === 'RJ' ? (
                  rioDeJaneiroCities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))
                ) : (
                  <option value="São Paulo">São Paulo</option>
                )}
              </select>
            </div>
            <div className="admin-form-group">
              <label htmlFor="edit-polo">Nome do Polo</label>
              <input
                id="edit-polo"
                type="text"
                value={editFormData.polo}
                onChange={(e) => setEditFormData({ ...editFormData, polo: e.target.value })}
                className="admin-input"
                placeholder="Ex: Polo Central"
              />
            </div>
            <div className="admin-form-group">
              <label htmlFor="edit-address">Endereço</label>
              <input
                id="edit-address"
                type="text"
                value={editFormData.address}
                onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                className="admin-input"
                placeholder="Ex: Rua Exemplo, 123"
              />
            </div>
            <div className="admin-form-group">
              <label htmlFor="edit-admin_name">Nome do Administrador</label>
              <input
                id="edit-admin_name"
                type="text"
                value={editFormData.admin_name}
                onChange={(e) => setEditFormData({ ...editFormData, admin_name: e.target.value })}
                className="admin-input"
                placeholder="Ex: João Silva"
              />
            </div>
            {formError && editingPolo && <p className="admin-form-error">{formError}</p>}
            <div className="admin-form-actions">
              <Button type="submit">Salvar Alterações</Button>
              <Button type="button" variant="ghost" onClick={cancelEditing}>
                Cancelar
              </Button>
            </div>
          </form>
        </section>
      )}

      <section className="admin-card">
        <div className="admin-card__head">
          <h2>Lista de Polos</h2>
          <Button variant="ghost" onClick={handleGeneratePDF} disabled={polos.length === 0}>
            Gerar PDF
          </Button>
        </div>

        <AdminFilterBar chips={chips} resultCount={filteredPolos.length} resultLabel="polos">
          <AdminSearchField
            id="polo-search"
            label="Busca"
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Polo, cidade, estado ou administrador"
          />
        </AdminFilterBar>

        {loading ? (
          <AdminLoadingState label="Carregando dados..." />
        ) : pagedData.length === 0 ? (
          <AdminEmptyState message="Nenhum polo encontrado." />
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID Polo</th>
                    <th>Estado</th>
                    <th>Cidade</th>
                    <th>Nome do Polo</th>
                    <th>Endereço</th>
                    <th>Nome do Administrador</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedData.map((polo) => (
                    <tr key={polo.id_acting}>
                      <td>{polo.id_acting}</td>
                      <td>{polo.state}</td>
                      <td>{polo.city}</td>
                      <td>{polo.polo}</td>
                      <td>{polo.address}</td>
                      <td>{polo.admin_name}</td>
                      <td>
                        <div className="admin-row-actions">
                          <button className="admin-row-action admin-row-action--edit" onClick={() => startEditing(polo)}>
                            Editar
                          </button>
                          <button className="admin-row-action admin-row-action--delete" onClick={() => handleDeletePolo(polo.id_acting)}>
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <AdminPagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        )}
      </section>
    </AdminLayout>
  );
};

export default PagePolo;
